/**
 * FragmentRenderer - Renders fragment blocks (alt, opt, loop, par) in SVG
 */

import type { Fragment } from '../../../parser/types';
import type { ParticipantLayout } from '../layout/types';
import type { InteractionLayout } from '../layout/types';

export interface FragmentRenderOptions {
  /**
   * Padding inside the fragment box
   */
  fragmentPadding: number;
  /**
   * Height of the fragment label section
   */
  fragmentLabelHeight: number;
  /**
   * Font size for fragment labels
   */
  fragmentLabelFontSize: number;
}

export const DEFAULT_FRAGMENT_OPTIONS: FragmentRenderOptions = {
  fragmentPadding: 16,
  fragmentLabelHeight: 32,
  fragmentLabelFontSize: 14,
};

export interface FragmentLayout {
  type: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  interactions: InteractionLayout[];
}

/**
 * Calculates the layout for a fragment box
 */
export function calculateFragmentLayout(
  fragment: Fragment,
  participantLayouts: Map<string, ParticipantLayout>,
  startY: number,
  interactionLayouts: InteractionLayout[],
  options: FragmentRenderOptions
): FragmentLayout | null {
  if (interactionLayouts.length === 0) {
    return null;
  }

  // Find all participants involved in this fragment
  const participantNames = new Set<string>();
  fragment.interactions.forEach((interaction) => {
    participantNames.add(interaction.from);
    participantNames.add(interaction.to);
  });

  // Get layouts for involved participants
  const layouts: ParticipantLayout[] = [];
  participantNames.forEach((name) => {
    const layout = participantLayouts.get(name);
    if (layout) {
      layouts.push(layout);
    }
  });

  if (layouts.length === 0) {
    return null;
  }

  // Sort by x coordinate to get leftmost and rightmost
  layouts.sort((a, b) => a.x - b.x);
  const leftmost = layouts[0];
  const rightmost = layouts[layouts.length - 1];

  if (!leftmost || !rightmost) {
    return null;
  }

  // Calculate fragment box dimensions
  const x = leftmost.x - options.fragmentPadding;
  const width =
    rightmost.x + rightmost.width - leftmost.x + options.fragmentPadding * 2;

  // Calculate height based on number of interactions
  const interactionSpacing = 80; // Should match layout config
  const height =
    options.fragmentLabelHeight +
    interactionLayouts.length * interactionSpacing +
    options.fragmentPadding;

  return {
    type: fragment.type,
    label: fragment.label,
    x,
    y: startY,
    width,
    height,
    interactions: interactionLayouts,
  };
}

/**
 * Renders a single fragment block
 */
export function renderFragment(
  fragmentLayout: FragmentLayout,
  options: FragmentRenderOptions
): string {
  const parts: string[] = [];

  parts.push(`<g class="fragment fragment-${fragmentLayout.type}">`);

  // Fragment box
  parts.push(
    `  <rect x="${fragmentLayout.x}" y="${fragmentLayout.y}" ` +
      `width="${fragmentLayout.width}" height="${fragmentLayout.height}" ` +
      `class="fragment-box" />`
  );

  // Label section (pentagonal shape on the left)
  const labelWidth = 80;
  const labelHeight = options.fragmentLabelHeight;
  const indent = 10;

  const labelPath = [
    `M ${fragmentLayout.x} ${fragmentLayout.y}`,
    `L ${fragmentLayout.x + labelWidth} ${fragmentLayout.y}`,
    `L ${fragmentLayout.x + labelWidth} ${fragmentLayout.y + labelHeight}`,
    `L ${fragmentLayout.x + indent} ${fragmentLayout.y + labelHeight}`,
    `L ${fragmentLayout.x} ${fragmentLayout.y + labelHeight - indent}`,
    'Z',
  ].join(' ');

  parts.push(`  <path d="${labelPath}" class="fragment-label-box" />`);

  // Fragment type text (e.g., "alt", "opt", "loop", "par")
  const labelX = fragmentLayout.x + labelWidth / 2;
  const labelY = fragmentLayout.y + labelHeight / 2;

  parts.push(
    `  <text x="${labelX}" y="${labelY}" ` +
      `class="fragment-type" ` +
      `text-anchor="middle" dominant-baseline="middle" ` +
      `font-size="${options.fragmentLabelFontSize}" ` +
      `font-weight="bold">${escapeXml(fragmentLayout.type)}</text>`
  );

  // Condition text (fragment label)
  const conditionX = fragmentLayout.x + labelWidth + 10;
  const conditionY = fragmentLayout.y + labelHeight / 2;

  parts.push(
    `  <text x="${conditionX}" y="${conditionY}" ` +
      `class="fragment-condition" ` +
      `dominant-baseline="middle" ` +
      `font-size="${options.fragmentLabelFontSize}" ` +
      `font-style="italic">[${escapeXml(fragmentLayout.label)}]</text>`
  );

  parts.push('</g>');

  return parts.join('\n');
}

/**
 * Renders multiple fragments for a step
 */
export function renderFragments(
  fragments: Fragment[],
  participantLayouts: Map<string, ParticipantLayout>,
  fragmentInteractionLayouts: Map<number, InteractionLayout[]>,
  startY: number,
  stepIndex: number,
  options: FragmentRenderOptions
): string {
  if (fragments.length === 0) {
    return '';
  }

  const parts: string[] = [];
  parts.push(`<g class="step-fragments" data-step="${stepIndex}">`);

  let currentY = startY;

  fragments.forEach((fragment, fragmentIndex) => {
    const interactionLayouts = fragmentInteractionLayouts.get(fragmentIndex);
    if (!interactionLayouts || interactionLayouts.length === 0) {
      return;
    }

    const layout = calculateFragmentLayout(
      fragment,
      participantLayouts,
      currentY,
      interactionLayouts,
      options
    );

    if (layout) {
      parts.push(renderFragment(layout, options));
      currentY += layout.height + options.fragmentPadding;
    }
  });

  parts.push('</g>');

  return parts.join('\n');
}

/**
 * Escapes XML special characters
 */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
